/**
 * Этот сервис хранит состояние фильтра
 * @class FilterService
 */

/**
 * @typedef {object} FilterItem
 * @property {string} type - тип фильтра: term, terms, range, geo
 * @property {*} value - значение фильтра, тип зависит от типа фильтра
 */

import _ from 'lodash';
import {Region, GeoItem} from '../../../../serp/classes';
import {GEO_TYPES} from '../../../../serp/constants';

angular.module('cian.filter')
  .factory('FilterService', [
    "$log",
    "$rootScope",
    "$q",
    "$window",
    "ApiService",
    "FilterObjectTypeService",
    "FilterDiffService",
    "FilterTagsService",
    "RegionUtilsService",
    "FilterTermsService",
    "GeoUtils",
    "HighwaysService",
    "AvailableFilters",
    "MetroService",
    "FilterConverterService",
    "QuickFilters",
    "TagTitleService",
    function ($log, $rootScope, $q, $window, ApiService, FilterObjectTypeService, FilterDiffService, FilterTagsService, RegionUtilsService, FilterTermsService, GeoUtils, HighwaysService, AvailableFilters, MetroService, FilterConverterService, QuickFilters, TagTitleService) {
    // С сервера приходит значение null для geo в базовом фильтре.
    // На самом деле geo фильтр представляет из себя массив объектов с разным типом (type) вида {id: 5, type: 'district', ...}
    // Нам удобнее для сравнения через jsondiffpatch (теги) и для директив, работающих с geo,
    // чтобы geo фильтр по-умолчанию имел значение [].
    var baseFilterOverride = {
      geo: {
        value: []
      },
      // Если пользователю доступны сортировки, то по-умолчанию проставляется галочка "Учитывать качество"
      quality: {
        value: $window._can_view_listing_sort ? true : null
      },
      // Так как бэк не знает о кастомных фильтрах, приходится добавлять их в базовый
      // Материалы дома для новостроек
      _house_material_new: {
        type: 'terms',
        value: []
      },
      // Группы шоссе
      _highway_directions: {
        type: 'terms',
        value: []
      }
    };

    // Базовый фильтр, содержит информацию о типе поля и значении по умолчанию
    // всего существует 6 типов базового фильтра
    // flatrent, flatsale, commercialrent, commercialsale, suburbanrent, suburbansale
    // Нужен для того, чтобы откатывать значения на правильные дефолты (не всегда дефолтное значение null)
    var baseFilter = angular.copy(baseFilterOverride);
    // Словарь "имя фильтра" -> {"тип фильтра", "значение по-умолчанию"}
    // Формируется из baseFilter каждый раз при смене этого baseFilter
    var filterInfoByKey = {};

    /** @namespace FilterService */
    var service = {
      /**
       * Модель фильтра
       * @type {object}
       */
      filter: {},

      /**
       * Предыдущий фильтр, нужен для получения разницы между состояниями фильтра,
       * которая в свою очередь нужна, чтобы понять, надо ли добавить/удалить теги
       * Смотри метод diffFiltersAndUpdateTags
       * @type {object}
       */
      previousFilter: {},

      /**
       * Модель фильтров, которая отправляется на сервер.
       * Получается исключением из service.filter полей со значениями по умолчанию
       * и небольшим преобразованием некоторых полей
       * @type {object}
       */
      query: {},

      /**
       * Тип сделки, возможные значения rent и sale
       * @type {?string}
       */
      dealType: null,

      /**
       * Категория объекта, возможные значения flat, suburban и commercial
       * @type {?string}
       */
      category: null,

      /**
       * Выбранные типы объектов
       * Может быть пусто, если выбрана категория целиком
       * @type {ObjectType[]}
       */
      objectTypes: [],

      /**
       * Выбранные регионы, например "Санкт-Петербург и область" или "Москва"
       * @type {Region[]}
       */
      selectedRegions: []
    };

    function setWatchers() {
      $rootScope.$watch(function () {
        return service.filter;
      }, function () {
        diffFiltersAndUpdateTags(service.previousFilter, service.filter);

        angular.copy(service.filter, service.previousFilter);

        updateQuery();
      }, true);

      // Если не важно, пешком или транспортом, то сбрасываем время
      $rootScope.$watch(
        function () {
          return service.filter.only_foot && service.filter.only_foot.value;
        },
        function (newValue, oldValue) {
          if (newValue !== oldValue) {
            service.filter.foot_min.value.lte = MetroService.getFootMinValue(service.filter.only_foot, service.filter.foot_min);
          }
        }
      );

      // Если удалили время до метро, то удаляем и способ
      $rootScope.$watch(
        function () {
          return service.filter.foot_min &&
                 service.filter.foot_min.value.lte;
        },
        function (newValue, oldValue) {
          if (newValue !== oldValue && (newValue === null || newValue === 0)) {
            nullifyFilters([
              'only_foot'
            ]);
          }
        }
      );

      // При смене вторички/новостройки сбрасываем фильтры, которые появляются/пропадают
      // TODO возможно перенести в AvailableFilters
      $rootScope.$watch(
          isNewBuilding,
          (newValue, oldValue) => {
            if (newValue !== oldValue) {
              nullifyFilters([
                'house_material', // отличается набор полей
                '_house_material_new',
                'kitchen',
                'lifts',
                'lift_passanger',
                'lift_service',
                'minbalkon',
                'balconies',
                'loggia',
                'windows_type',
                'wc',
                'wc_type',
                'repair',
                'parking_type',
                'ceiling_height',
                'house_year',
                'sost_type',
                'is_by_homeowner', // отсутствует на новостройках
                'is_in_hidden_base',
              ]);
            }
          }
      );

      // При смене типа гаража сбрасываем зависящие параметры
      $rootScope.$watchCollection(
        () => _.get(service.filter, 'garage_type.value'),
        (newVal, oldVal) => {
          if (newVal !== oldVal) {
            nullifyFilters([
              'garage_garage_type',
              'garage_material',
            ]);
          }
        }
      );

      // Линия домов видна только если выбран тип помещения "Street retail"
      $rootScope.$watchCollection(
        () => _.get(service.filter, 'placement_type.value'),
        (newVal, oldVal) => {
          if (newVal !== oldVal && !_.includes(newVal, 2)) {
            nullifyFilters([
              'building_house_line_type__shopping_area',
            ]);
          }
        }
      );
    }

    function setBaseFilter(newBaseFilter) {
      // Выкидываем свойство required из всех фильтров,
      // т.к. бэк его выкинуть отказался:
      // https://jira.cian.tech/browse/CD-3407
      angular.forEach(newBaseFilter, function(item) {
        delete item.required;
      });
      baseFilter = angular.merge({}, newBaseFilter, baseFilterOverride);
      filterInfoByKey = _.transform(baseFilter, function(memo, v, k) {
        if (k !== '_type') {
          memo[k] = {
            type: v.type,
            defaultValue: v.value
          }
        }
      }, {});
    }

    // Превращает серверную модель фильтров в клиентскую модель
    function getInitialFilter(query, additionalFiltersData) {
      if (additionalFiltersData) {
        // Преобразуем id, т.к. иногда они приходят как строки
        // (например, при заходе на страницу ЖК)
        additionalFiltersData = additionalFiltersData.map(data => ({
          ...data,
          id: Number(data.id),
        }));
      }

      return _.transform(query, function(memo, v, k) {
        if (k === 'geo') {
          memo[k] = _.defaults({
            value: v.value.map(geoFilterItem => {
              if (geoFilterItem.type === 'polygon') {
                const coordinatesAsNumbers = geoFilterItem.coordinates.map(
                  coord => coord.map(Number)
                );
                return GeoUtils.createPolygonObject([coordinatesAsNumbers], geoFilterItem.name);
              } else if (geoFilterItem.type === 'distance') {
                const centerAsNumbers = geoFilterItem.center.map(Number);
                return GeoUtils.createCircleObject(centerAsNumbers, geoFilterItem.radius, geoFilterItem.name);
              } else {
                return _.extend(geoFilterItem, _.find(additionalFiltersData, function(item) {
                  return item.type === geoFilterItem.type &&
                    item.id === geoFilterItem.id;
                }));
              }
            })
          }, v);
        } else {
          memo[k] = v;
        }
      }, {});
    }


    // Кастомные фильтры, чтобы проставлять при выборе сразу несколько значений
    const customFilterRules = [{
      original: 'house_material',
      custom: '_house_material_new',
      checkCondition: isNewBuilding
    }];


    // Метод превращающий модель фильтра в модель запроса для сервера
    function updateQuery() {
      const newQuery = FilterConverterService.convertToBackend(baseFilter, service.filter, customFilterRules);

      if (!angular.equals(service.query, newQuery)) {
        service.query = newQuery;
      }
    }

    function updateDealTypeAndCategory() {
      var typeInfo = service.filter._type.match('(flat|suburban|commercial)(rent|sale)');
      service.category = typeInfo[1];
      service.dealType = typeInfo[2];
    }

    function diffFiltersAndUpdateTags(a, b) {
      $log.debug('FilterService#diffFiltersAndUpdateTags', a, b);
      var diff = FilterDiffService.create(filterInfoByKey).diff(a, b);
      if (typeof diff === 'undefined') {
        return;
      }

      FilterTagsService.updateTags(diff, filterInfoByKey, service.getCurrentQuickFilters(), {
        isNewBuilding: service.isNewBuilding(),
        dealType: service.dealType,
      });
    }

    /**
     * @return {string[]} массив имён быстрых фильтров
     */
    service.getCurrentQuickFilters = () => QuickFilters.getControlsList({
      dealType: service.dealType,
      isShortTerm: service.isShortTerm(),
      category: service.category,
      objectNames: _.map(service.objectTypes, 'name'),
      currentRegions: service.selectedRegions,
      isNewBuilding: isNewBuilding(),
    });

    service.init = function (initialServerQueryModel, selectedRegions, additionalFiltersData) {
      setBaseFilter(ApiService.getDefaultFilterForType(initialServerQueryModel._type));

      service.previousFilter = angular.copy(baseFilter);
      service.filter = angular.copy(service.previousFilter);
      service.query = initialServerQueryModel;

      service.setRegions(selectedRegions);

      updateDealTypeAndCategory();

      if (initialServerQueryModel) {
        angular.extend(service.filter, getInitialFilter(angular.copy(initialServerQueryModel), additionalFiltersData));
        service.filter = FilterConverterService.convertFromBackend(baseFilter, service.filter, service.selectedRegions, customFilterRules);
      }

      // По текущему фильтру определяем выбранный тип объекта
      service.objectTypes = FilterObjectTypeService.determineObjectTypes(
        service.category, service.filter
      );

      setWatchers();
    };

    /**
     * @param {Region[]} newRegions
     */
    service.setRegions = function (newRegions) {
      // Ничего не делаем, если регионы те же
      if (RegionUtilsService.areRegionsEqual(newRegions, service.selectedRegions)) return;

      if (service.selectedRegions.length > 0) {
        // 1. При переключении "несовместимых регионов" (например, Москва → Татарстан)
        // сбарсываем гео-теги и всё, что зависит от региона
        // 2. При переключении/добавлении внутри "совместимых" регионов
        // (например, Москва + Московская область)
        // все выбранные ранее гео-теги остаются.
        if (!RegionUtilsService.areRegionsCompatible(service.selectedRegions, newRegions)) {
          nullifyFilters([
            // TODO не сбрасывать геотэги целиком:
            // сохранять у гео-объектов информацию об их родителе и при переключении сбрасывать только те, которые не относятся к новым регионам
            'geo',
            'only_foot', // Только там, где есть метро
            'foot_min', // Аналогично ^
            'sort', // есть опция "Расстояние до метро"
            'from_mcad_km', // Только в Москве
            'promo', // только МО и ЛО
            'from_developer', // только там, где новостройки
            'include_new_moscow',
            'bs_center',
            'bs_center_id',
          ]);
          FilterTagsService.toggleMetroTag(false);
        }

        // Сбрасываем флаг "Я - собственник" при выборе Москвы, МО, СПБ, ЛО или любых городов этих областей
        if (RegionUtilsService.isMoscowMOSpbLOOrAnyChildCityInTheseRegions(newRegions)) {
          nullifyFilters([
            'is_by_homeowner'
          ]);
        }
      }

      // Здесь хранится массив объектов регионов
      service.selectedRegions = _.sortBy(newRegions, 'id');
      // Здесь хранится массив id регионов
      service.filter.region.value = _.map(newRegions, 'id');
    };

    /**
     * Пытается добавить регион к текущим
     * @param {Region} newRegion
     */
    service.addRegion = newRegion => {
      // Если регион и так выбран, то ничего не меняем
      if (_.some(service.selectedRegions, { id: newRegion.id })) return;

      // Если уже выбраны гео-тэги, то пытаемся объединить текущие регионы с новым
      // Иначе просто переключаемся на новый регион
      if (_.get(service.filter, 'geo.value.length') > 0) {
        const regionsCombined = _.unionBy(service.selectedRegions, [newRegion], 'id');

        // Ищем группу (например, Москва + МО = Москва и область)
        if (RegionUtilsService.findGroup(regionsCombined)) {
          service.setRegions(regionsCombined);
          return;
        }

        // Если у регионов общий родитель, то переключаемся на него
        // Например, улица в Самаре + улица в Тольятти = 2 улицы в Самарской области
        const commonParent = RegionUtilsService.getCommonTopRegion(regionsCombined);
        if (commonParent) {
          service.setRegions([commonParent]);
          return;
        }
      }

      service.setRegions([newRegion]);
    };

    // 1. При переключении типа объекта внутри категории (жилая, загородная, коммерческая)
    // сохраняются все общие параметры поиска.
    // При этом если какой-то параметр, выбранный у первого типа объекта, не встречается у второго, он должен быть удален
    // (например, при переключении с "офиса" на "гараж" должен быть удален выбранный параметр класса строения,
    // при переключении с "дома" на "участок" должна быть удалена площадь дом и т.д.)
    // 2. При смене типа объекта внутри разных категорий (например, с квартиры на дом)
    // сохраняются параметры цены и гео, остальные удаляются.
    /**
     * @param {ObjectType[]} newObjectTypes
     */
    service.setObjectTypes = function (newObjectTypes) {
      updateFilter(service.dealType, newObjectTypes);
    };

    /**
     * @param {string} newDealType
     * @param {boolean} isShortTerm
     */
    service.setDealType = (newDealType, isShortTerm) => {
      const allowedCategories = FilterObjectTypeService.getObjectCategoriesForDeal(
        newDealType, isShortTerm
      );
      /** @type {ObjectCategory} */
      const selectedCategory = _.find(allowedCategories, { id: service.category });
      let newObjectTypes;

      if (selectedCategory) {
        // Проверяем, какие типы объектов доступны для нового типа сделки
        const objectsAreAllowed = service.objectTypes.length && _.every(
          service.objectTypes,
          objectType => _.find(selectedCategory.items, { name: objectType.name })
        );

        newObjectTypes = objectsAreAllowed ? service.objectTypes :
          // Если текущие объекты не доступны для нового типа сделки,
          // сбрасываем на дефолтный объект категории
          [FilterObjectTypeService.getObjectTypeByName(
            selectedCategory.defaultItem || selectedCategory.items[0].name
          )];
      } else {
        // Если выбранная категория недоступна для нового типа сделки,
        // то переключаемся на квартиру
        newObjectTypes = [FilterObjectTypeService.getObjectTypeByName('flat')];
      }

      updateFilter(newDealType, newObjectTypes, isShortTerm);
    };

    /**
     * @param {string} newDealType
     * @param {ObjectType[]} newObjectTypes
     * @param {boolean} [newIsShortTerm]
     */
    function updateFilter(newDealType, newObjectTypes, newIsShortTerm) {
      // Проверяем, что все объекты в одной категории
      const categories = _.keys(_.groupBy(newObjectTypes, 'category'));
      if (categories.length > 1) {
        console.error('Нельзя одновременно выбрать объекты из разных категорий:', newObjectTypes);
        return;
      }

      const newCategory = categories[0];
      const oldCategory = service.category;

      // Смена категории или типа сделки
      if (newCategory !== oldCategory || newDealType !== service.dealType) {
        service.category = newCategory;
        const newBaseFilter = ApiService.getDefaultFilterForType(
          service.category + newDealType
        );

        // Удаляем все теги для текущего типа объекта
        diffFiltersAndUpdateTags(service.filter, baseFilter);
        // Обновляем базовый фильтр (baseFilter теперь новое значение)
        setBaseFilter(newBaseFilter);
        // Следующее сравнение текущего фильтра будет относительно нового базового фильтра
        service.previousFilter = angular.copy(baseFilter);
      }


      /*
        Сброс фильтров
       */

      const prevFiltersList = AvailableFilters.getFiltersList(
        service.dealType,
        service.isShortTerm(),
        oldCategory,
        _.map(service.objectTypes, 'name'),
      );

      const newFiltersList = AvailableFilters.getFiltersList(
        newDealType,
        newIsShortTerm,
        newCategory,
        _.map(newObjectTypes, 'name'),
      );

      const filtersToKeep = _.intersection(prevFiltersList, newFiltersList);
      if (newDealType !== service.dealType) _.pull(filtersToKeep,
        'contract',
      );
      if (newCategory !== oldCategory) _.pull(filtersToKeep,
        'house_material',
        'total_area',
        'floorn',
      );

      service.reset({ ignore: filtersToKeep });


      /*
        Перезаписываем данные фильтра, определяющие текущий тип

        Исключения:
        1. Выбрано несколько объектов (новый объект добавляется к существующим данным)
        2. Выбран тот же объект, что и раньше (например, смена купли на продажу)
       */
      if (newObjectTypes !== service.objectTypes) {
        // Все одной категории, так что и название параметра одинаковое
        const filterParamName = newObjectTypes[0].filterParamName;
        const newValues =
          // Отбрасываем пустые значения (например, у квартиры нет своего value)
          _.map(newObjectTypes, 'filterParamValue').filter(angular.isDefined);

        // https://jira.cian.tech/browse/CSSSR-7771
        // TODO Думаю, что надо вместо room использовать искуственные/виртуальные параметры
        // для определения типа жилой и количества комнат.
        // При получении/отправке данных приводить данные апи к этим искуственным параметрам.
        // Также, стоит уточнить у бекенда, является ли room устаревшим параметром
        // для определения типов жилой недвижимости.
        // Конкретно для поля room сейчас есть конфликт - это поле отвечает как и за количество комнат,
        // так и за тип недвижимости (койко-место, комната, доля и т.д.)
        // Выражается это в следующей проблеме:
        // При смене квартиры на Квартиру в новостройке и наоборот теряется комнатность.
        // Сейчас делаю хот-фикс для квартиры.
        const previousAndNewObjectTypeIsFlat =
          newObjectTypes[0].name === 'flat' && newObjectTypes[0].name === service.objectTypes[0].name;
        if (!previousAndNewObjectTypeIsFlat) {
          service.filter[filterParamName].value =
            newValues.length ? newValues : null;
        }
      }

      // Проставляем срок аренды
      if (service.filter.for_day && angular.isDefined(newIsShortTerm)) {
        service.filter.for_day.value = newIsShortTerm ? '1' : '!1';
      }

      service.dealType = newDealType;
      service.objectTypes = newObjectTypes;
    }

    // Зануление списка фильтров
    function nullifyFilters(names) {
      angular.forEach(
        names,
        function (name) {
          var option = service.filter[name];
          if (option) {
            switch (option.type) {
              case 'range':
                option.value.gte = null;
                option.value.lte = null;
                break;
              case 'geo':
              case 'terms':
                option.value = [];
                break;
              default:
                option.value = null;
            }
          }
        }
      );
    }


    /* Проверка на ГЕО-тег
     ** type - (string)
     */
    function _isGeo(type) {
      return _.includes(_.values(GEO_TYPES), type);
    }

    /**
     * Добавить ГЕО фильтр
     * @param {GeoItem} data
     */
    service.add = data => {
      if (_isGeo(data.type)) {
        service.filter.geo.value = service.filter.geo.value || [];
        let alreadyExisted = service.filter.geo.value.some(
          newItem => {
            if (newItem.type === data.type) {
              // Если полигон, то сравниваем координаты, т.к. id у них генерятся уникальные
              if (data.type === GEO_TYPES.POLYGON) {
                return _.isEqual(data.coordinates, newItem.coordinates);
              }

              return newItem.id === data.id;
            }
          }
        );

        // Если шоссе, то не добавляем, если уже выбрано направление целиком
        if (!alreadyExisted && data.type === GEO_TYPES.HIGHWAY) {
          const directions = _.get(service.filter, '_highway_directions.value');
          if (directions) alreadyExisted = directions.some(
            dir => dir.highways.some(h => h.id === data.id)
          );
        }

        if (!alreadyExisted) {
          service.filter.geo.value.push(data);
        }
      }

      if (data.type === 'bs_center') {
        TagTitleService.saveTitle('bs_center', data.id, data.text);
        const BCFilter = service.filter.bs_center;
        BCFilter.value = _.union(BCFilter.value, [data.id]);
      }
    };

    // function _checkRegionInGeo() {
    //   for (var i = 0; i < service.filter.geo.value; i++) {
    //     var item = service.filter.geo.value[i];
    //     if (/street|/)
    //   }
    // }

    // Удалить фильтр (клик на крестик в списке тегов или удаление через особый дропдаун, например, метро или шоссе)
    service.remove = function (data) {
      if (data.type === 'geo') {
        var target = service.filter.geo.value;
        for (var i = target.length; i--;) {
          if (target[i].key === data.value.key && target[i].id === data.value.id) {
            target.splice(i, 1);
          }
        }
        const isAnyUndergrounds = target.filter(item => item.type === 'underground').length > 0;
        if (!isAnyUndergrounds) nullifyFilters(['only_foot', 'foot_min',]);
      } else {
        angular.forEach(service.filter, function (obj, key) {
          if (key === data.key) {
            switch (obj.type) {
              case 'term':
                // revert to baseFilter default value
                obj.value = baseFilter[key].value;
                break;
              case 'terms':
                _.remove(obj.value, x => _.isEqual(x, data.value));
                break;
              case 'range':
                obj.value.lte = null;
                obj.value.gte = null;
            }
          }
        });
      }
    };

    function fixIgnoreOption(allIgnore) {
      const allowedKeys = Object.keys(baseFilter);
      return _.intersection(allIgnore, allowedKeys);
    }

    service.reset = function (options) {
      options = _.merge({ // eslint-disable-line
        commonIgnore: ['region', 'engine_version', 'sort', 'quality'],
        ignore: []
      }, options || {});

      var allIgnore = options.commonIgnore.concat(options.ignore);
      var fixedAllIgnore = fixIgnoreOption(allIgnore);

      var frozenProps = _.pick(service.filter, fixedAllIgnore);
      service.filter = angular.merge({}, baseFilter, frozenProps);
    };

    /**
     * @param {string} category
     * @return {boolean}
     */
    service.categoryIs = function(category) {
      return service.category === category;
    };

    /**
     * @param {string} dealType
     * @return {boolean}
     */
    service.dealTypeIs = function(dealType) {
      return service.dealType === dealType;
    };

    /**
     * Проверяет, выбран ли данный объект (один или среди других)
     * @param {string} objectName
     * @return {boolean}
     */
    service.objectIsSelected = objectName =>
      _.includes(_.map(service.objectTypes, 'name'), objectName);

    /**
     * Проверяет, что выбрано только указанный объект
     * @param {string} objectName
     * @return {boolean}
     */
    service.selectedObjectIs = objectName => (
      service.objectTypes && service.objectTypes.length === 1 &&
      service.objectIsSelected(objectName)
    );

    /**
     * @return {boolean}
     */
    service.isShortTerm = function () {
      // Преобразуем, так как изначально приходит строка
      return !!(service.filter.for_day && parseInt(service.filter.for_day.value, 10) === 1);
    };

    service.isMO = function () {
      return RegionUtilsService.isMO(service.selectedRegions);
    };

    service.isHighwayAvailable = function() {
      if (service.isMO()) {
        return service.categoryIs('suburban');
      }

      return RegionUtilsService.selectedRegionHasHighway(service.selectedRegions);
    };

    service.isMetroAvailable = function() {
      return !service.categoryIs('suburban') && RegionUtilsService.selectedRegionHasMetro(service.selectedRegions);
    };

    function isFlatSale() {
      return service.dealTypeIs('sale') && service.categoryIs('flat');
    }

    function isOldBuilding() {
      return isFlatSale() &&
        service.filter.building_status &&
        service.filter.building_status.value === 1;
    }
    service.isOldBuilding = isOldBuilding;


    function isNewBuilding() {
      return isFlatSale() &&
        service.filter.building_status &&
        service.filter.building_status.value === 2;
    }
    service.isNewBuilding = isNewBuilding;

    return service;
  }]);



// WEBPACK FOOTER //
// ./src/components/filter/src/scripts/cian.filter.service.js